/**
 * EventTable — events as rows instead of calendar chips.
 *
 * Two hosts: the event list page, and the calendar's "Table View" toggle. The Figma
 * draws that toggle but has no frame behind it, so these columns are a judgement call
 * rather than a transcription. They are the six the filter bar already narrows by (type,
 * sub type, BU, city, status, owner) plus the dates — on the theory that a column
 * you can filter by is a column worth seeing, and that the table's job is to show what
 * the month grid cannot: events with no date at all.
 *
 * That last point is why the table exists and is not just a different skin on the
 * calendar. `startDate` is optional until PublishEvent, so a freshly created event is
 * invisible on the grid; the table is the only place it can be found and finished.
 *
 * Presentational and router-free, like EventCalendar: the host decides what clicking a
 * row means.
 */

import { useCollection } from '@amzn/awsui-collection-hooks';
import { useMemo } from 'react';
import Badge from '@amzn/awsui-components-react/polaris/badge';
import Box from '@amzn/awsui-components-react/polaris/box';
import Link from '@amzn/awsui-components-react/polaris/link';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import Table, { type TableProps } from '@amzn/awsui-components-react/polaris/table';
import type { Event } from '../../client/EventCalendarClient';
import { subTypeLabel, typeMetaOf } from '../../utils/eventTaxonomy';
import { NONE, formatRange } from '../../utils/eventDates';
import { EventTypeIcon } from '../EventTypeIcon';

/**
 * Orders two possibly-absent dates, treating "no date" as sorting after every real one.
 *
 * Note this is NOT "undated always last": Cloudscape applies the sort direction by
 * negating the comparator's result, so on a descending sort undated events move to the
 * TOP. That is left as it is rather than worked around — a comparator cannot see the
 * direction, and the behaviour reads correctly either way: ascending is "earliest first",
 * and descending is "most recent first", where an event whose date is still not set is
 * fairly the most outstanding thing on the list.
 */
function byDate(a: Date | undefined, b: Date | undefined): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.getTime() - b.getTime();
}

function statusCell(event: Event) {
  if (event.contentStatus === 'DRAFT') {
    // Deliberately not "grey": a draft is marketing-visible only, so it is a state that
    // needs acting on rather than a neutral one.
    return <Badge color="blue">Draft</Badge>;
  }
  if (event.contentStatus === 'PUBLISHED') return <Badge color="green">Published</Badge>;
  return <Box color="text-status-inactive">{NONE}</Box>;
}

function columns(
  onSelectEvent: ((event: Event) => void) | undefined,
  hideStatus: boolean,
): TableProps.ColumnDefinition<Event>[] {
  const all: TableProps.ColumnDefinition<Event>[] = [
    {
      id: 'eventName',
      header: 'Event Name',
      sortingField: 'eventName',
      minWidth: 240,
      cell: (e) => {
        const name = e.eventName || '(untitled)';
        // A real href, so the row can be opened in a new tab — but the host handles the
        // follow, which keeps this component independent of the router.
        if (!onSelectEvent || !e.eventId) return name;
        return (
          <Link
            href={`/event/events/${e.eventId}`}
            onFollow={(ev) => {
              ev.preventDefault();
              onSelectEvent(e);
            }}
          >
            {name}
          </Link>
        );
      },
    },
    {
      id: 'eventType',
      header: 'Event Type',
      sortingComparator: (a, b) =>
        (typeMetaOf(a)?.label ?? '').localeCompare(typeMetaOf(b)?.label ?? ''),
      cell: (e) => {
        const meta = typeMetaOf(e);
        if (!meta) return '—';
        return (
          <span className="ec-legend-item">
            <EventTypeIcon type={e.eventType!} /> {meta.label}
          </span>
        );
      },
    },
    {
      id: 'eventSubType',
      header: 'Event Sub Type',
      sortingField: 'eventSubType',
      cell: (e) => subTypeLabel(e.eventSubType),
    },
    {
      id: 'dates',
      header: 'Date',
      sortingComparator: (a, b) => byDate(a.startDate, b.startDate),
      minWidth: 180,
      cell: (e) => formatRange(e.startDate, e.endDate),
    },
    {
      // The design's "Segment" column; the value is the event's BUs.
      id: 'bu',
      header: 'BU',
      cell: (e) => (e.businessUnits?.length ? e.businessUnits.join(', ') : NONE),
    },
    { id: 'city', header: 'City', sortingField: 'city', cell: (e) => e.city || NONE },
    {
      id: 'contentStatus',
      header: 'Status',
      sortingField: 'contentStatus',
      cell: statusCell,
    },
    {
      id: 'eventOwner',
      header: 'Marketing Event Owner',
      sortingField: 'eventOwner',
      cell: (e) => e.eventOwner || NONE,
    },
  ];
  return hideStatus ? all.filter((c) => c.id !== 'contentStatus') : all;
}

export interface EventTableProps {
  /** The events to show — already filtered by the host. */
  events: readonly Event[];
  loading?: boolean;
  /** Called when a row's name is clicked. Omit it and the names are plain text. */
  onSelectEvent?: (event: Event) => void;
  /**
   * Shown when there is nothing to list. The host knows whether that is "no events
   * exist" or "your filters matched none", which need different wording.
   */
  empty?: React.ReactNode;
  /** Rendered above the table by the host (title, actions). */
  header?: React.ReactNode;
  /** Drops the Status column — for a BD, who sees published events only. */
  hideStatus?: boolean;
}

/** Big enough that the usual month's worth of events fits on one page. */
const PAGE_SIZE = 25;

export default function EventTable({
  events,
  loading,
  onSelectEvent,
  empty,
  header,
  hideStatus = false,
}: EventTableProps) {
  /* Memoised, and not just to save work: useCollection tracks the sorted column by
     IDENTITY. Rebuilding the definitions every render handed it a fresh
     `sortingComparator` function each time, so the column it had recorded as sorted no
     longer matched anything — the header showed aria-sort="none" and a second click
     could not flip the direction. Columns that sort by `sortingField` (a string) were
     unaffected, which is why only Date and Event Type were stuck. */
  const columnDefinitions = useMemo(
    () => columns(onSelectEvent, hideStatus),
    [onSelectEvent, hideStatus],
  );

  // `events` is a readonly view; useCollection wants a mutable array.
  const { items, paginationProps, collectionProps } = useCollection([...events], {
    pagination: { pageSize: PAGE_SIZE },
    sorting: {},
  });

  return (
    <Table
      {...collectionProps}
      items={items}
      columnDefinitions={columnDefinitions}
      trackBy={(e) => e.eventId ?? e.eventName ?? ''}
      loading={loading}
      loadingText="Loading events"
      variant="embedded"
      header={header}
      empty={
        empty ?? (
          <Box textAlign="center" padding={{ vertical: 'l' }} color="text-status-inactive">
            No events
          </Box>
        )
      }
      // Paging only appears once it can do something.
      pagination={
        events.length > PAGE_SIZE ? (
          <Pagination
            {...paginationProps}
            // Not optional in practice: without these Cloudscape renders the arrows and
            // page buttons with aria-label="", so a screen reader announces an unnamed
            // button. There is no sensible default it could pick, hence no warning.
            ariaLabels={{
              nextPageLabel: 'Next page',
              previousPageLabel: 'Previous page',
              pageLabel: (n) => `Page ${n}`,
            }}
          />
        ) : undefined
      }
    />
  );
}
